import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Fingerprint, Mail, Lock, LogIn, Shield, Heart } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

export const PatientLoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleLogin = async (method) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsLoading(false);
    toast.success("Accesso effettuato!");
    navigate("/patient");
  };

  const handleEmailLogin = (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Compila tutti i campi");
      return;
    }
    handleLogin("email");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 md:py-12">
        {/* Logo */}
        <div className="mb-8 animate-fade-in-up">
          <img 
            src={LOGO_URL} 
            alt="WitApp" 
            className="h-16 md:h-20 w-auto"
          />
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md animate-fade-in-up" style={{ animationDelay: "100ms" }}>
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Portale Paziente</CardTitle>
            <CardDescription className="text-base">
              Accedi per visualizzare i tuoi esami
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Biometric button */}
            <Button
              variant="premium"
              size="lg"
              className="w-full gap-3"
              onClick={() => handleLogin("biometric")}
              disabled={isLoading}
            >
              <Fingerprint className="h-5 w-5" />
              Accesso Biometrico
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">oppure</span>
              </div>
            </div>

            {/* Email login form */}
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="la-tua-email@example.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                className="w-full gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
                ) : (
                  <LogIn className="h-5 w-5" />
                )}
                Accedi
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground">
              Non hai un account?{" "}
              <a href="#" className="text-primary hover:underline">Registrati</a>
            </p>
          </CardContent>
        </Card>

        {/* Trust indicators */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-xs">Dati Protetti</span>
          </div>
        </div>
      </div>

      <footer className="py-4 text-center text-xs text-muted-foreground">
        <p>© 2024 WitApp. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
};

export default PatientLoginPage;
